import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { improveDescription, PromptType } from "api/subscription/correctionApi";
import axios from "axios";
import { ExpanderWithHeightTransition } from "components/ExpanderWithHeightTransition";
import { cx } from "lib/cx";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { toggleReset } from "lib/redux/settingsSlice";
import {
  selectAccessToken,
  selectIsPremiumUser,
  setIsAuthenticationOpen,
  setIsExplorePlansOverlayOpen,
  setIsOutOfCreditsModalOpen,
  setOAuthOpenedFromButton,
  setSubscriptionCreditCount,
} from "lib/redux/uiSlice";
import { convertToHtmlList } from "lib/utils/convertToHtmlList";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import PremiumSparkle from "public/assets/premium-sparkle.svg";

export interface AiSuggestionProps<K extends string, V extends string[]> {
  section: string;
  description: string[];
  onChange: (field: K, value: V) => void;
  setAlert: (alert: string) => void;
}

const suggestionPrompts = [
  { label: "Fix Grammar", prompt: PromptType.FIX_SPELLING_AND_GRAMMAR },
  { label: "Add Words", prompt: PromptType.MAKE_LONGER },
  { label: "Less Words", prompt: PromptType.MAKE_SHORTER },
  { label: "Quantify Impact", prompt: PromptType.QUANTIFY_IMPACT },
];

const toneOptions = [
  { label: "Professional", prompt: PromptType.MAKE_SOUND_MORE_PROFESSIONAL },
  { label: "Confident", prompt: PromptType.MAKE_SOUND_MORE_CONFIDENT },
  { label: "Casual", prompt: PromptType.MAKE_SOUND_MORE_CASUAL },
];

export const AiSuggestion = <T extends string>({
  section,
  description,
  onChange,
  setAlert,
}: AiSuggestionProps<T, string[]>) => {
  const dispatch = useAppDispatch();
  const [suggestedDescriptions, setSuggestedDescriptions] = useState<
    string[] | null
  >(null);
  const [isSuggestionBoxOpen, setIsSuggestionBoxOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isToneDropdownOpen, setIsToneDropdownOpen] = useState(false);
  const customPromptRef = useRef<HTMLTextAreaElement>(null);
  const accessToken = useAppSelector(selectAccessToken);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);
  const [isPremiumTheme, setIsPremiumTheme] = useState(false);
  const [numberOfCustomPromptCharacters, setNumberOfCustomPromptCharacters] =
    useState(0);

  // theme will be premium only when the user is logged in and is a premium user
  useEffect(() => {
    if (accessToken && isPremiumUser) {
      setIsPremiumTheme(true);
    } else {
      setIsPremiumTheme(false);
    }
  }, [accessToken, isPremiumUser]);

  useEffect(() => {
    if (isSuggestionBoxOpen) {
      setShowAnimation(true);
    } else {
      setShowAnimation(false);
    }
  }, [isSuggestionBoxOpen]);

  const handleSuggestion = async (prompt: PromptType, promptText?: string) => {
    if (!accessToken) {
      dispatch(setOAuthOpenedFromButton("use Ai Suggestions"));
      dispatch(setIsAuthenticationOpen(true));
      return;
    }

    const descriptionToImprove = suggestedDescriptions ?? description;

    if (
      !descriptionToImprove ||
      (descriptionToImprove.length === 0 && prompt !== "CUSTOM")
    ) {
      setAlert(`Please enter a valid description`);
      return;
    }

    // if the promptText is empty and there is no descriptionToImprove then show an alert and return
    if (!promptText && prompt === "CUSTOM") {
      setAlert("Please enter a valid prompt");
      return;
    }

    try {
      setIsSuggestionBoxOpen(true);
      setLoading(true);

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("Request timeout"));
        }, 3000); // 3 seconds timeout
      });

      const requestPromise = improveDescription({
        prompt,
        promptText,
        section,
        descriptions: descriptionToImprove,
      });

      // Race between timeout and actual request
      const response = await Promise.race([
        timeoutPromise,
        requestPromise,
      ]).catch((error) => {
        throw error;
      });

      const { improvedDescriptions } = response;
      setSuggestedDescriptions(improvedDescriptions);
      // clear the custom prompt textarea if it is not empty after the suggestion
      if (customPromptRef.current?.value) {
        customPromptRef.current.value = "";
      }
      dispatch(setSubscriptionCreditCount(response.credits));
    } catch (error) {
      setIsSuggestionBoxOpen(false);
      if ((error as Error).message === "Request timeout") {
        setAlert("Request took too long. Please try again.");
      } else if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          // dispatch(setIsOutOfCreditsModalOpen(true));
          dispatch(setIsExplorePlansOverlayOpen(true));
          return;
        }
      }

      setAlert("An error occurred. Please try again.");
      console.error("Error improving description:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSuggestedDescription = () => {
    const htmlDescriptions = [
      convertToHtmlList(suggestedDescriptions as string[]),
    ];
    onChange("descriptions" as T, htmlDescriptions);
    dispatch(toggleReset());
    setSuggestedDescriptions(null);
    setIsSuggestionBoxOpen(false);
  };

  const handleDiscardSuggestedDescription = () => {
    setSuggestedDescriptions(null);
    setIsSuggestionBoxOpen(false);
  };

  // send the custom prompt to the server if user clicks enter button
  const handleCustomPromptEnter = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = customPromptRef.current?.value?.trim();
      if (trimmedValue) {
        handleSuggestion("CUSTOM" as PromptType, trimmedValue);
      }
    }
  };

  useEffect(() => {
    const textArea = customPromptRef.current;

    if (!textArea) return;
    textArea.addEventListener("keydown", handleCustomPromptEnter);

    return () => {
      textArea.removeEventListener("keydown", handleCustomPromptEnter);
    };
  }, [customPromptRef]);

  const handleCustomPromptCharacterCount = (
    e: React.FormEvent<HTMLTextAreaElement>
  ) => {
    const value = (e.target as HTMLTextAreaElement).value;
    setNumberOfCustomPromptCharacters(value.length);
  };

  return (
    <>
      {/* FETCHED AI SUGGESTIONS */}
      {isSuggestionBoxOpen && (
        <div
          className={cx(
            "mb-3 flex w-full transform flex-col gap-5 rounded-3xl px-7 py-6 transition-all duration-700 ease-out",
            isPremiumUser
              ? "bg-gradient-to-tr from-[#0038FF] to-[#FF9399]"
              : "bg-[#eeeeee]",
            showAnimation
              ? "translate-y-0 scale-100 opacity-100"
              : "translate-y-5 scale-90 opacity-0"
          )}
        >
          {isPremiumTheme && (
            <PremiumSparkle className="inline-block h-4 w-4" />
          )}
          {!loading && suggestedDescriptions ? (
            <>
              <ul
                className={cx(
                  "flex flex-col gap-4 font-montserrat text-[10px] font-medium md:text-sm",
                  isPremiumTheme ? "text-white" : "text-[#002E60]"
                )}
              >
                {suggestedDescriptions.map((description, index) => (
                  <li
                    key={index}
                    className="text-fade-in"
                    style={{ animationDelay: `${index * 300}ms` }}
                  >
                    &bull; {description}
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-2 font-poppins text-xs font-medium md:text-sm">
                <button
                  className="rounded-2xl bg-[#f9f9f9] px-3 py-1 shadow-md"
                  onClick={handleDiscardSuggestedDescription}
                >
                  Discard
                </button>
                <button
                  onClick={handleAcceptSuggestedDescription}
                  className={cx(
                    "flex items-center rounded-2xl bg-gradient-to-tr from-[#ECA1FF] to-[#923CFF] px-3 py-1  text-white",
                    isPremiumTheme && "border border-white"
                  )}
                >
                  {isPremiumTheme && <SparklesIcon className="h-4 w-4" />}
                  Accept
                </button>
              </div>
            </>
          ) : (
            <div className="mx-auto w-fit animate-pulse rounded-full bg-white px-6 py-3 text-center text-xs font-semibold text-gray-700 shadow-lg md:text-base">
              Loading Suggestions...
            </div>
          )}
        </div>
      )}

      {/* AI SUGGESTION OPTIONS */}
      <ul
        className={cx(
          "relative flex flex-wrap justify-center gap-2 rounded-3xl px-2 py-4 font-montserrat text-[10px] font-medium md:text-xs",
          isPremiumTheme
            ? "bg-gradient-to-tr from-[#1479D6] via-[#2E96F6]/90 to-[#8AC7FF]"
            : "border border-gray-300"
        )}
      >
        {suggestionPrompts.map(({ label, prompt }) => (
          <li
            key={prompt}
            className={cx(
              "w-24 cursor-pointer whitespace-nowrap rounded-[22px] bg-white py-1 text-center text-[#0245F1] shadow-2xl hover:font-semibold hover:shadow-2xl active:bg-[#0E23DF] active:text-white md:w-32",
              isPremiumTheme
                ? ""
                : "border border-[#0245F1] hover:border hover:border-[#0245F1]"
            )}
            onClick={() => handleSuggestion(prompt)}
          >
            {label}
          </li>
        ))}
        <li
          className={cx(
            "relative w-24 cursor-pointer rounded-[22px] bg-white py-1 md:w-32",
            isPremiumTheme ? "" : "border border-[#0245F1]"
          )}
        >
          <span
            className="mx-auto flex h-full justify-center gap-1 text-[#0245F1] hover:font-semibold hover:shadow-2xl"
            onClick={() => setIsToneDropdownOpen((prev) => !prev)}
          >
            Refine Tone{" "}
            {isToneDropdownOpen ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </span>
          <ExpanderWithHeightTransition
            expanded={isToneDropdownOpen}
            overflowHidden={false}
          >
            <ul className="absolute left-1/2 top-8 z-[10] flex -translate-x-1/2 flex-col rounded-md border border-gray-300 bg-white p-2 shadow-md">
              {toneOptions.map(({ label, prompt }) => (
                <li
                  key={prompt}
                  className="cursor-pointer rounded-[22px] border-[#0245F1] px-3 py-1 text-[#0245F1] hover:border hover:font-semibold hover:shadow-2xl active:bg-[#0E23DF] active:text-white active:text-white"
                  onClick={() => handleSuggestion(prompt)}
                >
                  {label}
                </li>
              ))}
            </ul>
          </ExpanderWithHeightTransition>
        </li>
        <div className="flex w-full items-center gap-3">
          <div className="relative flex-1">
            <textarea
              placeholder="Type your prompt"
              className="w-full resize-none overflow-hidden rounded-3xl border border-[#EBEBEB] px-4 py-3 font-montserrat text-[10px] drop-shadow-sm focus:outline-gray-300 md:text-xs"
              ref={customPromptRef}
              rows={1}
              onInput={(e) => {
                handleCustomPromptCharacterCount(e);
                (e.target as HTMLTextAreaElement).style.height = "auto"; // Reset height
                (e.target as HTMLTextAreaElement).style.height = `${
                  (e.target as HTMLTextAreaElement).scrollHeight
                }px`; // Set new height based on content
              }}
            />
            {/* <span className="absolute bottom-2 right-3 text-gray-700">
              {numberOfCustomPromptCharacters}
            </span> */}
          </div>
          <button
            className="rounded-full bg-white p-2 shadow-lg drop-shadow-2xl hover:border hover:border-[#0245F1] hover:text-[#0245F1] active:bg-[#0245F1] active:text-white"
            onClick={() =>
              handleSuggestion(
                "CUSTOM" as PromptType,
                customPromptRef.current?.value!
              )
            }
          >
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
        <span className="absolute -bottom-2 left-3 flex items-center gap-[2px] rounded-md bg-white px-1 py-[1px] font-montserrat text-[10px] text-[#4C00EF]">
          <ClipboardIcon className="h-3 w-3" /> Ai Assitance
        </span>
      </ul>
    </>
  );
};

export const AiSuggestionCTA = () => {
  return (
    <div className="mx-auto mt-3 flex w-fit items-center gap-5">
      <p className="font-montserrat text-[10px] font-semibold text-[#666565] md:text-sm">
        Craft the best Resume with Ai
      </p>
      <Link
        href="/premium-subscription"
        className="rounded-2xl bg-gradient-to-br from-[#ECA1FF] to-[#923CFF] px-3 py-[6px] font-poppins text-[8px] font-medium text-white md:text-xs"
      >
        Upgrade to Pro
      </Link>
    </div>
  );
};
